import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useState } from 'react';
import {
  generatePolicyPaymentObjects,
  regeneratePolicyPaymentObjects
} from '../../../../api/requests';
import DatePickerInput from '../../../../components/form-control/DatePickerInput';
import { INPUT_DATE_FORMAT } from '../../../../resources/constants';
import Button from '../../../../components/form-control/Button';
import { IRequestResponse } from '../../../../resources/interfaces';
import { showToast } from '../../../../utils/miscFunctions';

interface RegeneratePaymentObjectsProps {
  onProceed: () => void;
  onClose: () => void;
  policy: any;
  action: string;
}

const RegeneratePaymentObjectsModal = (props: RegeneratePaymentObjectsProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);

  const regeneratePaymentObjects = (paymentDate: string) => {
    if (props.policy?.ref) {
      regeneratePolicyPaymentObjects(props.policy?.ref, paymentDate)
        .then((response: IRequestResponse) => {
          showToast.success(response?.message ?? 'Payment schedule generated successfully.');
          props.onProceed();
        })
        .catch((error: any) => {
          showToast.error(
            error?.description ?? 'Failed to generate payment schedules. Please try again later.'
          );
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const generatePaymentObjects = (paymentDate: string) => {
    if (props.policy?.ref) {
      generatePolicyPaymentObjects(props.policy?.ref, paymentDate)
        .then((response: IRequestResponse) => {
          showToast.success(response?.message ?? 'Payment schedule generated successfully.');
          props.onProceed();
        })
        .catch((error: IRequestResponse) => {
          showToast.error(
            error.description ?? 'Failed to generate payment schedules. Please try again later.'
          );
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const handleAction = (paymentDate: string) => {
    switch (props.action) {
      case 'GENERATE_PAYMENT_OBJECTS':
        generatePaymentObjects(paymentDate);
        break;

      case 'REGENERATE_PAYMENT_OBJECTS':
        regeneratePaymentObjects(paymentDate);
        break;

      default:
        break;
    }
  };

  return (
    <div className="bg-white rounded-xl dark:bg-darkBg dark:border dark:border-darkBg-border">

      <div className="pt-5">
        <span className="text-center px-4 dark:text-darkBg-mainText">Select policy start date</span>
      </div>

      <Formik
        initialValues={{
          startsOn: new Date()
        }}
        validationSchema={Yup.object().shape({
          startsOn: Yup.date()
            .typeError('Date type error')
            .required('Policy start date is required')
        })}
        onSubmit={(values) => {
          setSubmitting(true);

          const paymentDate = values.startsOn
            ? moment(values.startsOn).format(INPUT_DATE_FORMAT)
            : '';

          handleAction(paymentDate);
        }}>
        {({ values, errors, isSubmitting, handleSubmit, handleChange, handleBlur, setFieldValue, touched }) => (
          <form className="px-4 py-5 bg-white dark:bg-darkBg space-y-4 w-full rounded-xl" onSubmit={handleSubmit}>
            <DatePickerInput
              id={'startsOn'}
              label="Payment start date"
              selected={values.startsOn}
              onChange={(date: Date | null) => {
                setFieldValue('startsOn', date);
              }}
              classes="w-full"
              showYearDropdown
              allowEarlierSelections={true}
              error={
                touched.startsOn && errors.startsOn !== undefined
                  ? String(errors.startsOn)
                  : undefined
              }
              helperText={
                touched.startsOn && errors.startsOn !== undefined
                  ? String(errors.startsOn)
                  : undefined
              }
              dateFormat={'dd/MM/yyyy'}
            />

            <div className="flex justify-end w-full">
              <Button
                label="Submit"
                type="submit"
                bgColor='primary'
                loading={submitting}
                disabled={submitting}
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default RegeneratePaymentObjectsModal;
